import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Save, X } from 'lucide-react';
import { EditCostBudgetForm } from './edit-cost-budget-form';
import { EditSalesBudgetForm } from './edit-sales-budget-form';
import { EditResourceBudgetForm } from './edit-resource-budget-form';
import { BudgetRevisionPanel } from './budget-revision-panel';
import type { BudgetData, ProjectType } from '@/types/budget.types';

interface EditBudgetModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  budgetData: BudgetData;
  projectType: ProjectType;
  onSave: (updatedBudget: Partial<BudgetData>, revisionReason: string, saveAsBaseline: boolean) => Promise<void>;
}

export function EditBudgetModal({
  open,
  onOpenChange,
  budgetData,
  projectType,
  onSave,
}: EditBudgetModalProps) {
  const [activeTab, setActiveTab] = useState('cost');
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Track changes for each budget type
  const [costBudgetChanges, setCostBudgetChanges] = useState<any>(null);
  const [salesBudgetChanges, setSalesBudgetChanges] = useState<any>(null);
  const [resourceBudgetChanges, setResourceBudgetChanges] = useState<any>(null);
  
  // Revision details
  const [revisionReason, setRevisionReason] = useState('');
  const [saveAsBaseline, setSaveAsBaseline] = useState(false);

  // Calculate total change percentage
  const calculateChangePercentage = () => {
    if (!costBudgetChanges) return 0;
    
    const originalTotal = budgetData.costBudget?.totals?.budget || 0;
    const newTotal = costBudgetChanges.totals?.budget || originalTotal;
    
    if (originalTotal === 0) return 0;
    
    return Math.abs(((newTotal - originalTotal) / originalTotal) * 100);
  };

  const changePercentage = calculateChangePercentage();
  const requiresApproval = changePercentage > 5;

  const handleSave = async (asDraft: boolean = false) => {
    try {
      setIsSaving(true);
      setError(null);

      // Validate revision reason if required
      if (!asDraft && requiresApproval && !revisionReason.trim()) {
        setError('Please provide a reason for this budget change (required for changes >5%)');
        return;
      }

      // Combine all changes
      const updatedBudget: Partial<BudgetData> = {};
      
      if (costBudgetChanges) {
        updatedBudget.costBudget = costBudgetChanges;
      }
      
      if (salesBudgetChanges) {
        updatedBudget.salesBudget = salesBudgetChanges;
      }
      
      if (resourceBudgetChanges) {
        updatedBudget.resourceBudget = resourceBudgetChanges;
      }

      await onSave(updatedBudget, revisionReason, saveAsBaseline);
      
      // Reset state
      setIsDirty(false);
      setCostBudgetChanges(null);
      setSalesBudgetChanges(null);
      setResourceBudgetChanges(null);
      setRevisionReason('');
      setSaveAsBaseline(false);
      
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save budget');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to cancel?'
      );
      if (!confirmed) return;
    }
    
    // Reset state
    setIsDirty(false);
    setCostBudgetChanges(null);
    setSalesBudgetChanges(null);
    setResourceBudgetChanges(null);
    setRevisionReason('');
    setSaveAsBaseline(false);
    setError(null);
    
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Project Budget</DialogTitle>
          <DialogDescription>
            Update budget values for {budgetData.projectName}. Changes greater than 5% require approval.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="cost">Cost Budget</TabsTrigger>
            <TabsTrigger value="sales">Sales Budget</TabsTrigger>
            <TabsTrigger value="resource">Resource Budget</TabsTrigger>
            <TabsTrigger value="revision">Revision Details</TabsTrigger>
          </TabsList>

          <TabsContent value="cost" className="space-y-4">
            <EditCostBudgetForm
              costBudget={budgetData.costBudget}
              onChange={(changes) => {
                setCostBudgetChanges(changes);
                setIsDirty(true);
              }}
            />
          </TabsContent>

          <TabsContent value="sales" className="space-y-4">
            <EditSalesBudgetForm
              salesBudget={budgetData.salesBudget}
              projectType={projectType}
              onChange={(changes) => {
                setSalesBudgetChanges(changes);
                setIsDirty(true);
              }}
            />
          </TabsContent>

          <TabsContent value="resource" className="space-y-4">
            <EditResourceBudgetForm
              resourceBudget={budgetData.resourceBudget}
              onChange={(changes) => {
                setResourceBudgetChanges(changes);
                setIsDirty(true);
              }}
            />
          </TabsContent>

          <TabsContent value="revision" className="space-y-4">
            <BudgetRevisionPanel
              changePercentage={changePercentage}
              requiresApproval={requiresApproval}
              revisionReason={revisionReason}
              onRevisionReasonChange={setRevisionReason}
              saveAsBaseline={saveAsBaseline}
              onSaveAsBaselineChange={setSaveAsBaseline}
              costBudgetChanges={costBudgetChanges}
              salesBudgetChanges={salesBudgetChanges}
              resourceBudgetChanges={resourceBudgetChanges}
              originalBudget={budgetData}
            />
          </TabsContent>
        </Tabs>

        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            {isDirty && (
              <span className="text-amber-600">
                • Unsaved changes ({changePercentage.toFixed(1)}% change)
              </span>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            
            <Button
              variant="outline"
              onClick={() => handleSave(true)}
              disabled={!isDirty || isSaving}
            >
              <Save className="h-4 w-4 mr-2" />
              Save as Draft
            </Button>
            
            <Button
              onClick={() => handleSave(false)}
              disabled={!isDirty || isSaving}
            >
              <Save className="h-4 w-4 mr-2" />
              {requiresApproval ? 'Submit for Approval' : 'Save Budget'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
